<template>
  <PForm
    ref="pForm"
    :heading="headingLabel"
    :primary-cta="addButton"
    :secondary-cta="{}"
    :ternary-cta="RemoveBankButton"
    @close="onClose"
    @reset="resetForm"
    @submit.prevent="onSubmit"
  >
    <template #header-info>
      <PBadge
        v-if="formState.edit && props.bank?.is_primary"
        rounded
        class="text-sm text-weight-medium q-ma-xxs"
        label="Primary"
        color="primary-30"
        text-color="primary"
      />
    </template>
    <div class="row q-col-gutter-xl">
      <div class="col-6">
        <PInput
          v-model.trim="formData.account_number"
          label="Bank Account Number"
          placeholder="Enter bank account number"
          :validations="{
            lengths: [9, 17],
          }"
          required
          @update:model-value="onFieldUpdate('account_number')"
        ></PInput>
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.ifsc_code"
          debounce="300"
          label="Bank IFSC Code"
          placeholder="Enter IFSC code"
          required
          :validations="{
            type: 'ifsc',
            length: 11,
            required: true,
          }"
          @update:model-value="onFieldUpdate('ifsc_code')"
        ></PInput>
      </div>
      <div class="col-6">
        <PSelectInput
          v-model="formData.bank_name"
          :options="unref(bankFilter.entities)"
          label="Bank Name"
          option-label="bank_name"
          placeholder="Select Bank"
          required
          @filter="bankFilter.onFilter"
          @filter-abort="bankFilter.onFilterAbort"
          @update:model-value="onBankUpdate"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model="formData.bank_beneficiary_name"
          label="Beneficiary Name"
          placeholder="Enter beneficiary name"
          required
          :loading="bankVerificationLoading"
          :error="!!bankVerificationError"
          @update:model-value="onFieldUpdate('bank_beneficiary_name')"
        ></PInput>
      </div>

      <div class="col-12">
        <PInput
          v-model.trim="formData.branch_address"
          label="Branch"
          placeholder="Enter branch address"
          type="textarea"
          required
          autogrow
          :error="!!ifscApiError"
          :error-message="ifscApiError"
          @update:model-value="onFieldUpdate('branch_address')"
        />
      </div>
      <div class="col-12">
        <PFile
          ref="fileComponent"
          v-model="fileModelValue"
          variant="compact"
          :label="
            documentData.name
              ? useChangeCase(documentData.name || '', 'capitalCase').value
              : ''
          "
          class="q-mt-sm"
          :max-files="1"
          :required="documentData?.required"
          accept=".pdf, .jpeg, .jpg, .png"
          help="Primary bank account cancelled cheque"
          help-classes="text-xs text-neutral-80 q-mt-xs"
          @view-file="viewFile"
        >
          <template #action-icon>
            <UploadIcon class="icon--md" />
          </template>
        </PFile>
      </div>
      <div v-if="formState.edit && !props.bank?.is_primary" class="col-12">
        <PCheckbox
          v-model="computedIsPrimary"
          :disable="!banks.length"
          label="Make this primary bank account for payments"
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import addBankForm from "../models/addBankForm";
import UploadIcon from "assets/icons/re-upload.svg";
import { externalBanks } from "src/api";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useQuasar } from "quasar";
import * as bankApi from "src/api/bank";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { isEmpty } from "src/assets/scripts/utilities/helpers";
import { ifsc as ifscCode } from "src/assets/scripts/utilities/validationRules";

import { useBankVerification } from "src/modules/user/composables/bankVerification";

const $q = useQuasar();
const pForm = ref(null);
let bankFilter = ref({});
const bankDetailsLoading = ref(false);
const { formState, user } = storeToRefs(useUserStore());
const { addBankFormState, banks } = storeToRefs(useBankStore());
const { getDocumentDetails } = useUserStore();
const { toggleAddBankForm, postBank, editBank, getBanks } = useBankStore();

const { viewFile } = useFile();
let documentData = ref({});

defineExpose({ pForm });

const trackFormChanges = ref(true);
const isOnlyPrimaryChanged = ref(true);
const ifscApiError = ref("");
const ifscApiErrorMsg = ref(
  "Unable to fetch branch address. Please enter the details manually.",
);

const props = defineProps({
  bank: {
    type: Object,
    default: () => {
      return {};
    },
  },
  removePopup: {
    type: Function,
    default: () => {
      return {};
    },
  },
  isRemoveLoading: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const computedBank = computed(() => {
  return props.bank;
});

const { formData, closeForm, submitForm, resetForm, isLoading } = useForm({
  formModel: addBankForm(),
  entity: computedBank,
  formRef: pForm,
  isForm: addBankFormState,
  post: postBank,
  put: editBank,
  toggleForm: toggleAddBankForm,
});

const { verifyBankAccount, bankVerificationLoading, bankVerificationError } =
  useBankVerification(formData, addBankFormState);

const headingLabel = computed(() => {
  return unref(addBankFormState).edit ? "Edit Bank" : "Add Bank";
});

const computedIsPrimary = computed({
  get() {
    if (!unref(banks)?.length) {
      unref(formData).is_primary = true;
    }

    return unref(formData).is_primary;
  },
  set(newValue) {
    unref(formData).is_primary = newValue;

    trackFormChanges.value = false;
  },
});

const getUploadDocumentDetails = async () => {
  const code = "cancelled_cheque";
  const data = await getDocumentDetails(code);

  documentData.value = data?.data?.data[0];
};

const addButton = computed(() => {
  return {
    label: unref(addBankFormState).edit ? "Update" : "Submit",
    disable: unref(trackFormChanges) || !isFormValid(),
    loading: unref(isLoading),
  };
});

const fetchBankDetails = async () => {
  const ifsc = unref(formData).ifsc_code;

  try {
    ifscApiError.value = "";

    if (!ifsc) {
      unref(formData).bank_name = "";

      unref(formData).branch_address = "";

      return;
    }

    if (ifsc.length !== 11 || !ifscCode(ifsc)) {
      unref(formData).bank_name = "";

      unref(formData).branch_address = "";

      ifscApiError.value = ifscApiErrorMsg.value;

      return;
    }

    bankDetailsLoading.value = true;

    const response = await bankApi.fetchBankDetailsFromIFSC(ifsc);
    const findBank = unref(bankFilter).entities.filter(
      (bank) => bank.bank_name === response.data.BANK,
    );

    if (findBank?.length) {
      unref(formData).bank_name = unref(findBank)[0].bank_name;

      unref(formData).branch_address = response.data.ADDRESS || "";

      if (!unref(formData).branch_address) {
        ifscApiError.value = ifscApiErrorMsg.value;
      }

      unref(formData).code = unref(findBank)[0].code;
    }
  } catch (error) {
    console.error(error);

    unref(formData).bank_name = "";

    unref(formData).branch_address = "";

    ifscApiError.value = ifscApiErrorMsg.value;
  } finally {
    bankDetailsLoading.value = false;
  }
};

const initBankFilter = async () => {
  const externalBank = await externalBanks.index();

  bankFilter.value = useFilter({
    entities: externalBank.data?.data,
    entity: "externalBanks",
    props: ["bank_name"],
  });
};

const fileComponent = ref(null);

const fileModelValue = computed({
  get() {
    return formData.value.file || null;
  },
  set(value) {
    formData.value.file = value || null;
  },
});

const onFieldUpdate = (field) => {
  if (field === "ifsc_code") {
    fetchBankDetails();
  }

  if (field === "account_number" || field === "ifsc_code") {
    verifyBankAccount();
  }

  if (field === "bank_beneficiary_name") {
    bankVerificationError.value = "";
  }

  if (field === "branch_address") {
    ifscApiError.value = "";

    trackFormChanges.value = false;

    return;
  }

  if (unref(formData).file) {
    unref(formData).file = null;
  }

  trackFormChanges.value = false;

  isOnlyPrimaryChanged.value = false;
};

const onBankUpdate = (bank) => {
  const { bank_name = "", code = "" } = bank;

  formData.value.bank_name = bank_name;

  formData.value.code = code;

  onFieldUpdate();
};

const RemoveBankButton = computed(() => {
  if (unref(addBankFormState).edit && !props.bank?.is_primary)
    return {
      label: "Remove",
      color: "error-70",
      flat: true,
      align: "left",
      loading: props.isRemoveLoading[props.bank._id],
      onClick: async () => {
        await props.removePopup(unref(formData));
      },
    };
  else return {};
});

const onClose = () => {
  ifscApiError.value = "";

  trackFormChanges.value = true;

  closeForm(true);
};

const onSubmit = () => {
  const isEdit = unref(addBankFormState).edit;
  const bankName = unref(formData)?.bank_name;

  const showConfirmationDialog = (title, text) => {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        title,
        text,
        isFooterVisible: true,
        labelClass: "bg-none",
        cardStyle: { minWidth: "600px" },
        submittable: true,
        cancellable: true,
        closable: true,
        onConfirm: () => onSubmitForm(),
      },
    });
  };

  if (
    unref(banks)?.length &&
    !unref(props.bank)?.is_primary &&
    unref(formData).is_primary &&
    !(!isEdit && unref(banks)?.length > 0 && unref(formData).is_primary)
  ) {
    showConfirmationDialog(
      `Primary Bank Account Confirmation`,
      `Are you sure you want to make ${bankName} your primary bank account?`,
    );
  } else if (
    (isEdit && unref(formData).files?.length) ||
    props?.bank?.file?.length
  ) {
    showConfirmationDialog(
      `Edit Bank Details`,
      `Are you sure you want to update bank details for ${bankName}?`,
    );
  } else {
    onSubmitForm();
  }
};

const onSubmitForm = async () => {
  const isEdit = unref(addBankFormState).edit;

  if (isEmpty(unref(formData).file)) {
    return;
  }

  unref(formData).bank_beneficiary_name =
    unref(formData).bank_beneficiary_name.trim();

  unref(formData).file_category = unref(documentData).category;

  unref(formData).file_type = unref(documentData).code;

  unref(formData).shop_name = unref(user).basicInfo.shop_name;

  if (!unref(trackFormChanges) || !isEdit) {
    unref(formData).document_id = unref(documentData)._id;

    unref(formData).file_id = props?.bank?.file?._id;
  }

  const primaryChangeCheck =
    unref(formData).is_primary !== props.bank?.is_primary;

  if (primaryChangeCheck && isEdit && unref(isOnlyPrimaryChanged)) {
    try {
      await bankApi.putPrimaryBank(unref(user)._id, {
        is_primary: unref(formData).is_primary,
        bank_beneficiary_name: unref(formData).bank_beneficiary_name,
        status_cd: unref(formData).status_cd,
        bank_name: unref(formData).bank_name,
        code: unref(formData).code,
        ifsc_code: unref(formData).ifsc_code,
        account_number: unref(formData).account_number,
        _id: unref(formData)._id,
      });

      closeForm(true);

      getBanks();
    } catch (error) {
      console.error("Error updating primary bank:", error);
    }

    return;
  }

  try {
    await submitForm();

    isOnlyPrimaryChanged.value = true;
  } catch (error) {
    console.error("Error submitting form:", error);
  }
};

const isFormValid = () => {
  const { bank_beneficiary_name, ifsc_code, bank_name, account_number, file } =
    formData.value;

  const isFileSelected = !isEmpty(file);
  const isEdit = unref(addBankFormState).edit;
  const primaryChangeCheck =
    unref(formData).is_primary !== props.bank?.is_primary;

  return (
    (bank_beneficiary_name &&
      ifsc_code &&
      bank_name &&
      account_number &&
      isFileSelected) ||
    (primaryChangeCheck && isEdit && unref(isOnlyPrimaryChanged))
  );
};

watchEffect(() => {
  initBankFilter();

  getUploadDocumentDetails();
});
</script>
